import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

// 2.5D extrusion of unit hotspots into a stacked 3D massing model.
// Uses raw three.js (no react-three-fiber) to avoid React 19 JSX conflicts.
//
// Features:
//  - Preset camera views (Top / Iso / Front) and a Reset button
//  - Floor isolation (show one floor, dim others)
//  - Explode animation (separate floors vertically)
//  - Status filter chips (show only selected statuses)
//  - Unit-code labels on each block (via CSS2DRenderer)
//  - Export PNG of the current view
//  - Click a block to open the unit

const FLOOR_KEYS = ['G', 'A', 'B', 'C'];
const FLOOR_LABELS = { G: 'Ground', A: '1st Fl', B: '2nd Fl', C: '3rd Fl' };
const FLOOR_HEIGHT = 8;
const FOOTPRINT = 100;

const STATUS_COLOR = {
  for_sale: '#22c55e', rental: '#3b82f6', subsidized: '#a855f7',
  do_not_sell: '#9ca3af', rental_or_sale: '#eab308', reserved: '#f97316', sold: '#ef4444',
};
const STATUS_LABELS = {
  for_sale: 'For Sale', rental: 'Rental', subsidized: 'Subsidized',
  do_not_sell: 'Do Not Sell', rental_or_sale: 'Rental or Sale', reserved: 'Reserved', sold: 'Sold',
};
const TYPE_LABELS = {
  studio: 'Studio', '1_bed': '1 Bed', '2_bed': '2 Bed', '3_bed': '3 Bed',
  commercial: 'Commercial', office: 'Office', retail: 'Retail',
};

// Preset camera positions. Target is the building center.
const PRESETS = {
  iso: { pos: [130, 100, 130] },
  top: { pos: [FOOTPRINT / 2, 220, FOOTPRINT / 2 + 0.001] },
  front: { pos: [FOOTPRINT / 2, 40, 220] },
  side: { pos: [220, 40, FOOTPRINT / 2] },
};

export default function MassingView({ units, onSelectUnit }) {
  const containerRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const sceneRef = useRef({});
  const onSelectRef = useRef(onSelectUnit);
  onSelectRef.current = onSelectUnit;

  const [hoveredUnit, setHoveredUnit] = useState(null);
  const [isolatedFloor, setIsolatedFloor] = useState(null);  // null | 'G' | 'A' | 'B' | 'C'
  const [exploded, setExploded] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [hiddenStatuses, setHiddenStatuses] = useState(new Set());

  const placed = useMemo(
    () => units.filter(u => u.hotspot && FLOOR_KEYS.includes(u.hotspot.plan)),
    [units]
  );

  // Status counts for filter chips (only statuses actually on the model)
  const statusCounts = useMemo(() => {
    const c = {};
    placed.forEach(u => { c[u.status] = (c[u.status] || 0) + 1; });
    return c;
  }, [placed]);

  const toggleStatus = (s) => {
    setHiddenStatuses(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };

  // ---- build / teardown scene when placed units change ----
  useEffect(() => {
    if (!canvasWrapRef.current || placed.length === 0) return;
    const container = canvasWrapRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#0a0a14');

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(...PRESETS.iso.pos);

    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    // CSS2D layer for unit labels
    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(width, height);
    labelRenderer.domElement.style.position = 'absolute';
    labelRenderer.domElement.style.top = '0';
    labelRenderer.domElement.style.left = '0';
    labelRenderer.domElement.style.pointerEvents = 'none';
    container.appendChild(labelRenderer.domElement);

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dir1 = new THREE.DirectionalLight(0xffffff, 1.2);
    dir1.position.set(80, 120, 60);
    scene.add(dir1);
    const dir2 = new THREE.DirectionalLight(0xffffff, 0.4);
    dir2.position.set(-60, 80, -40);
    scene.add(dir2);

    // Ground plane
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(FOOTPRINT * 1.4, FOOTPRINT * 1.4),
      new THREE.MeshStandardMaterial({ color: 0x0a0a14 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(FOOTPRINT / 2, -0.5, FOOTPRINT / 2);
    scene.add(ground);

    // Floor plates (translucent slabs so empty floors are visible)
    const plates = [];
    FLOOR_KEYS.forEach((f, i) => {
      const plate = new THREE.Mesh(
        new THREE.BoxGeometry(FOOTPRINT + 4, 0.4, FOOTPRINT + 4),
        new THREE.MeshStandardMaterial({ color: 0x1a1a2e, transparent: true, opacity: 0.55 })
      );
      plate.position.set(FOOTPRINT / 2, i * FLOOR_HEIGHT, FOOTPRINT / 2);
      plate.userData = { isPlate: true, floor: f, baseY: i * FLOOR_HEIGHT };
      scene.add(plate);
      plates.push(plate);
    });

    // Unit blocks
    const meshes = [];
    placed.forEach(u => {
      const h = u.hotspot;
      const w = (h.w / 100) * FOOTPRINT;
      const d = (h.h / 100) * FOOTPRINT;
      const cx = (h.x / 100) * FOOTPRINT + w / 2;
      const cz = (h.y / 100) * FOOTPRINT + d / 2;
      const cy = FLOOR_KEYS.indexOf(h.plan) * FLOOR_HEIGHT + FLOOR_HEIGHT / 2;
      const color = new THREE.Color(STATUS_COLOR[u.status] || '#a78bfa');
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(w, FLOOR_HEIGHT * 0.85, d),
        new THREE.MeshStandardMaterial({
          color, emissive: color, emissiveIntensity: 0.15,
          opacity: 0.82, transparent: true,
        })
      );
      mesh.position.set(cx, cy, cz);
      mesh.userData = { unit: u, baseY: cy };

      // Unit-code label
      const labelDiv = document.createElement('div');
      labelDiv.textContent = u.unit_code;
      labelDiv.style.cssText = `
        font: bold 10px "IBM Plex Mono", monospace;
        color: #000; background: ${STATUS_COLOR[u.status] || '#a78bfa'};
        padding: 1px 4px; border-radius: 3px; white-space: nowrap;
        pointer-events: none; box-shadow: 0 1px 3px rgba(0,0,0,.4);
      `;
      const label = new CSS2DObject(labelDiv);
      label.position.set(0, FLOOR_HEIGHT * 0.45 + 0.5, 0);
      mesh.add(label);
      mesh.userData.label = label;
      mesh.userData.labelDiv = labelDiv;

      scene.add(mesh);
      meshes.push(mesh);
    });

    // Orbit controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(FOOTPRINT / 2, FLOOR_HEIGHT * 1.5, FOOTPRINT / 2);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.maxPolarAngle = Math.PI / 2 - 0.05;
    controls.minDistance = 30;
    controls.maxDistance = 400;
    controls.update();

    // Raycaster picking
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hoveredMesh = null;

    const onPointerMove = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(meshes.filter(m => m.visible));
      const newHover = hits[0]?.object || null;
      if (newHover !== hoveredMesh) {
        hoveredMesh = newHover;
        setHoveredUnit(newHover ? newHover.userData.unit : null);
        renderer.domElement.style.cursor = newHover ? 'pointer' : 'default';
      }
    };
    const onClick = () => {
      if (hoveredMesh && onSelectRef.current) {
        onSelectRef.current(hoveredMesh.userData.unit);
      }
    };
    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('click', onClick);

    // Animation loop: smooth hover + floor explode + isolate opacity
    let rafId = 0;
    const animate = () => {
      controls.update();

      const state = sceneRef.current;
      const isolated = state.isolatedFloor;
      const exp = state.exploded ? 1 : 0;
      const showLbl = state.showLabels !== false;
      const hidden = state.hiddenStatuses || new Set();

      // Animate plates
      plates.forEach(p => {
        const floorIdx = FLOOR_KEYS.indexOf(p.userData.floor);
        const targetY = p.userData.baseY + exp * floorIdx * 6;
        p.position.y += (targetY - p.position.y) * 0.15;
        const targetOpacity = isolated && isolated !== p.userData.floor ? 0.15 : 0.55;
        p.material.opacity += (targetOpacity - p.material.opacity) * 0.2;
      });

      // Animate unit meshes
      meshes.forEach(m => {
        const u = m.userData.unit;
        const floorIdx = FLOOR_KEYS.indexOf(u.hotspot.plan);
        const explodeOffset = exp * floorIdx * 6;
        const isHover = m === hoveredMesh;
        const isIsolatedOut = isolated && isolated !== u.hotspot.plan;
        const isHiddenStatus = hidden.has(u.status);
        const shouldHide = isHiddenStatus;

        // Hard-hide via visibility
        m.visible = !shouldHide;
        if (m.userData.label) m.userData.label.visible = !shouldHide && showLbl;

        const targetY = m.userData.baseY + explodeOffset + (isHover ? 1.5 : 0);
        m.position.y += (targetY - m.position.y) * 0.15;
        const targetEmissive = isHover ? 0.45 : 0.15;
        m.material.emissiveIntensity += (targetEmissive - m.material.emissiveIntensity) * 0.2;
        const baseOpacity = isIsolatedOut ? 0.12 : (isHover ? 0.96 : 0.82);
        m.material.opacity += (baseOpacity - m.material.opacity) * 0.2;
        if (m.userData.labelDiv) {
          m.userData.labelDiv.style.opacity = isIsolatedOut ? '0.25' : '1';
        }
      });

      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };

    sceneRef.current = {
      scene, camera, renderer, labelRenderer, controls, meshes, plates,
      isolatedFloor, exploded, showLabels, hiddenStatuses,
    };
    animate();

    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      labelRenderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('click', onClick);
      controls.dispose();
      meshes.forEach(m => {
        m.geometry.dispose();
        m.material.dispose();
      });
      plates.forEach(p => {
        p.geometry.dispose();
        p.material.dispose();
      });
      ground.geometry.dispose();
      ground.material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      if (labelRenderer.domElement.parentNode) labelRenderer.domElement.parentNode.removeChild(labelRenderer.domElement);
    };
  }, [placed]);

  // Sync live state into the animation loop
  useEffect(() => {
    if (sceneRef.current) {
      sceneRef.current.isolatedFloor = isolatedFloor;
      sceneRef.current.exploded = exploded;
      sceneRef.current.showLabels = showLabels;
      sceneRef.current.hiddenStatuses = hiddenStatuses;
    }
  }, [isolatedFloor, exploded, showLabels, hiddenStatuses]);

  // ---- camera preset actions ----
  const applyPreset = useCallback((key) => {
    const s = sceneRef.current;
    if (!s.camera || !s.controls) return;
    const [x, y, z] = PRESETS[key].pos;
    animateCamera(s.camera, s.controls, { x, y, z }, { x: FOOTPRINT / 2, y: FLOOR_HEIGHT * 1.5, z: FOOTPRINT / 2 });
  }, []);

  const resetView = useCallback(() => {
    setIsolatedFloor(null);
    setExploded(false);
    setHiddenStatuses(new Set());
    applyPreset('iso');
  }, [applyPreset]);

  const exportPng = useCallback(() => {
    const s = sceneRef.current;
    if (!s.renderer) return;
    // Force an explicit render in case we're between frames
    s.renderer.render(s.scene, s.camera);
    const dataUrl = s.renderer.domElement.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `massing-view-${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
  }, []);

  if (placed.length === 0) {
    return (
      <div className="rounded-lg border border-white/10 py-12 text-center" style={{ color: 'var(--text)' }}>
        <p className="text-sm font-medium opacity-80">No units to render in 3D yet.</p>
        <p className="text-xs opacity-60 mt-1">Place hotspots on each floor plan first — they'll extrude into 3D blocks here automatically.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-2" data-testid="massing-view">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <div className="flex items-center gap-1 rounded-md bg-black/40 border border-white/10 p-1">
          <span className="opacity-60 px-1 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text)' }}>View</span>
          <ToolBtn onClick={() => applyPreset('iso')} testid="view-iso">Iso</ToolBtn>
          <ToolBtn onClick={() => applyPreset('top')} testid="view-top">Top</ToolBtn>
          <ToolBtn onClick={() => applyPreset('front')} testid="view-front">Front</ToolBtn>
          <ToolBtn onClick={() => applyPreset('side')} testid="view-side">Side</ToolBtn>
        </div>

        <div className="flex items-center gap-1 rounded-md bg-black/40 border border-white/10 p-1">
          <span className="opacity-60 px-1 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text)' }}>Isolate floor</span>
          <ToolBtn onClick={() => setIsolatedFloor(null)} active={!isolatedFloor} testid="isolate-all">All</ToolBtn>
          {FLOOR_KEYS.map(f => (
            <ToolBtn key={f} active={isolatedFloor === f}
              onClick={() => setIsolatedFloor(prev => prev === f ? null : f)}
              testid={`isolate-${f}`}>
              {FLOOR_LABELS[f]}
            </ToolBtn>
          ))}
        </div>

        <ToolBtn onClick={() => setExploded(e => !e)} active={exploded} testid="toggle-explode">
          {exploded ? '🔺 Collapse' : '🔻 Explode'}
        </ToolBtn>
        <ToolBtn onClick={() => setShowLabels(v => !v)} active={showLabels} testid="toggle-labels">
          {showLabels ? 'Hide Labels' : 'Show Labels'}
        </ToolBtn>
        <ToolBtn onClick={resetView} testid="reset-view">Reset</ToolBtn>
        <ToolBtn onClick={exportPng} testid="export-png">📷 Export PNG</ToolBtn>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="opacity-60 px-1 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text)' }}>Filter:</span>
        {Object.entries(statusCounts).map(([s, count]) => {
          const off = hiddenStatuses.has(s);
          return (
            <button key={s} onClick={() => toggleStatus(s)}
              className="rounded-full px-2 py-0.5 border transition-all"
              style={{
                background: off ? 'transparent' : `${STATUS_COLOR[s]}22`,
                borderColor: STATUS_COLOR[s],
                color: off ? 'var(--text)' : STATUS_COLOR[s],
                opacity: off ? 0.4 : 1,
                textDecoration: off ? 'line-through' : 'none',
              }}
              data-testid={`filter-${s}`}>
              <span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: STATUS_COLOR[s] }} />
              {STATUS_LABELS[s] || s} · {count}
            </button>
          );
        })}
      </div>

      {/* Canvas container with overlays */}
      <div ref={canvasWrapRef} style={{
        position: 'relative', height: 540, borderRadius: 8, overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.1)',
      }}>
        <HoverInfo unit={hoveredUnit} />
        <div style={{
          position: 'absolute', bottom: 10, right: 12,
          background: 'rgba(0,0,0,0.55)', borderRadius: 6, padding: '4px 8px',
          fontSize: 10, color: '#fff', opacity: 0.85, pointerEvents: 'none', zIndex: 3,
        }}>
          Drag to rotate · Scroll to zoom · Click a block
        </div>
      </div>
    </div>
  );
}

// Smooth-animate camera to a new position/target over ~400ms
function animateCamera(camera, controls, toPos, toTarget) {
  const fromPos = camera.position.clone();
  const fromTarget = controls.target.clone();
  const end = new THREE.Vector3(toPos.x, toPos.y, toPos.z);
  const endTarget = new THREE.Vector3(toTarget.x, toTarget.y, toTarget.z);
  const duration = 450;
  const start = performance.now();
  const step = () => {
    const t = Math.min(1, (performance.now() - start) / duration);
    const e = 1 - Math.pow(1 - t, 3); // easeOutCubic
    camera.position.lerpVectors(fromPos, end, e);
    controls.target.lerpVectors(fromTarget, endTarget, e);
    controls.update();
    if (t < 1) requestAnimationFrame(step);
  };
  step();
}

function ToolBtn({ children, onClick, active = false, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-2 py-1 rounded text-[11px] whitespace-nowrap transition-colors"
      style={{
        background: active ? 'var(--accent)' : 'rgba(255,255,255,0.05)',
        color: active ? '#000' : 'var(--text)',
        border: '1px solid rgba(255,255,255,0.1)',
        fontWeight: active ? 700 : 500,
      }}
    >
      {children}
    </button>
  );
}

function HoverInfo({ unit }) {
  if (!unit) return null;
  const color = STATUS_COLOR[unit.status] || '#a78bfa';
  const lines = [
    `${unit.unit_code} · ${TYPE_LABELS[unit.type] || unit.type}`,
    STATUS_LABELS[unit.status] || unit.status,
  ];
  if (unit.price) lines.push(`Sale: $${Number(unit.price).toLocaleString()}`);
  if (unit.monthly_rent) lines.push(`Rent: $${Number(unit.monthly_rent).toLocaleString()}/mo`);
  return (
    <div style={{
      position: 'absolute', top: 10, left: 10,
      background: 'rgba(0,0,0,0.85)', border: `1px solid ${color}`,
      borderRadius: 6, padding: '6px 10px',
      fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#fff', lineHeight: 1.6,
      pointerEvents: 'none', zIndex: 3,
    }}>
      {lines.map((l, i) => (
        <div key={i} style={i === 0 ? { color, fontWeight: 'bold' } : { opacity: 0.85 }}>{l}</div>
      ))}
    </div>
  );
}
